import { useRef, useMemo } from 'react';
import Select, { components } from 'react-select';
import { getIn } from 'formik';

const SearchableDropdown = ({
  label,
  name,
  options = [],
  isMulti = false,
  isClearable = true,
  formik,
  onSelect = () => {},
  onDelete = () => {},
}) => {
  const selectRef = useRef(null);
  const selectAllOption = { label: 'Select All', value: '*' };

  const currentValue = formik.values[name] || (isMulti ? [] : null);
  const previousValue = useRef(
    isMulti
      ? options.filter(opt => currentValue.includes(opt.value))
      : options.find(opt => opt.value === currentValue) || null
  );

  const allOptions = useMemo(() => {
    const selected = previousValue.current || [];
    const selectedCount = isMulti ? selected.length : selected ? 1 : 0;
    const showSelectAll = isMulti && selectedCount < options.length;
    return isMulti
      ? (showSelectAll ? [selectAllOption, ...options] : options)
      : options;
  }, [options, isMulti]);
  const rawVal = getIn(formik.values, name);  
  const toStr = v => (v == null ? '' : String(v));
  const getValue = () => {
    return (isMulti
      ? options.filter(o => Array.isArray(rawVal) && rawVal.map(toStr).includes(toStr(o.value)))
      : options.find(o => toStr(o.value) === toStr(rawVal)) || null);
  };

  const handleChange = (selected) => {
    let finalSelected;
    if (isMulti) {
      const current = Array.isArray(selected) ? selected : [];
      const prev = previousValue.current || [];

      const isSelectAll = current.some(opt => opt.value === '*');

      if (isSelectAll) {
        finalSelected =
          prev.length === options.length ? [] : [...options];
        selectRef.current?.blur(); // Close menu
      } else {
        finalSelected = current;
      }

      // Track added/removed items
      const added = finalSelected.filter(curr => !prev.some(p => p.value === curr.value));
      const removed = prev.filter(p => !finalSelected.some(curr => curr.value === p.value));

      added.forEach(({ value }) => onSelect(value));
      removed.forEach(({ value }) => onDelete(value));

      const formikValue = finalSelected.map(opt => opt.value);
      formik.setFieldValue(name, formikValue);
      previousValue.current = finalSelected;
    } else {
      finalSelected = selected;
      formik.setFieldValue(name, selected?.value || null);
      previousValue.current = selected;
    }
  };

  const Option = (props) => {
    const { data, isSelected, innerRef, innerProps, selectOption } = props;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          selectOption(data);
        }}
        style={{
          backgroundColor: props.isFocused ? '#eee' : 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{ marginRight: 8 }}
        />
        {data.label}
      </div>
    );
  };

  return (
    <div className="mb-3">
      {label && <label className="form-label">{label}</label>}
      <Select
        ref={selectRef}
        name={name}
        options={allOptions}
        isMulti={isMulti}
        isClearable={isClearable}
        value={getValue()}
        onChange={handleChange}
  onBlur={() => formik.setFieldTouched ? formik.setFieldTouched(name, true) : undefined}
        components={isMulti ? { Option } : undefined}
        closeMenuOnSelect={!isMulti}
        menuPortalTarget={document.body}
        styles={{
          container: base => ({ ...base, width: '100%' }),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
      />
      {formik.touched[name] && formik.errors[name] && (
        <div className="text-danger mt-1">{formik.errors[name]}</div>
      )}
    </div>
  );
};

export default SearchableDropdown;
